import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { Badge } from '../../../components/ui/badge'
import { getLocalApiUrl, authFetch } from '../../../config/api'

import { formatSydneyDate } from '../../../utils/sydneyDateTime'
function fmtDate(v) {
  if (!v) return '—'
  try {
    return formatSydneyDate(new Date(v))
  } catch {
    return String(v)
  }
}

export default function ProfileSowSection({ masterProfileId }) {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    async function load() {
      const id = String(masterProfileId || '').trim()
      if (!id) {
        setLoading(false)
        setData(null)
        return
      }
      setLoading(true)
      setError('')
      try {
        const res = await authFetch(
          getLocalApiUrl(
            `/api/profiles/${encodeURIComponent(id)}/source-of-wealth?history=1`
          )
        )
        const j = await res.json()
        if (cancelled) return
        if (!res.ok || !j.success) throw new Error(j.error || 'Failed to load SoW')
        setData(j.data)
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Failed to load SoW')
          setData(null)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [masterProfileId])

  const current = data?.current
  const open = data?.openOutreach
  const history = Array.isArray(data?.history) ? data.history.filter((h) => h.status !== 'current' || h.id !== current?.id) : []

  let statusLabel = 'No SoW on file'
  let statusClass = 'bg-slate-100 text-slate-700 border-slate-200'
  if (open) {
    statusLabel = open.isOverdue || open.status === 'overdue' ? 'Outreach overdue' : 'Outreach in progress'
    statusClass =
      open.isOverdue || open.status === 'overdue'
        ? 'bg-red-100 text-red-800 border-red-200'
        : 'bg-amber-100 text-amber-900 border-amber-200'
  } else if (current) {
    if (current.source === 'meeting') {
      statusLabel = 'Meeting requested / recorded'
      statusClass = 'bg-orange-100 text-orange-900 border-orange-200'
    } else {
      statusLabel = 'SoW on file'
      statusClass = 'bg-emerald-100 text-emerald-800 border-emerald-200'
    }
  }

  return (
    <Card className="border-l-4 border-l-teal-500 mt-6">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between gap-3 text-base">
          <span className="flex items-center gap-2">
            <span aria-hidden>💰</span>
            Source of Wealth
          </span>
          <Badge className={`text-xs ${statusClass}`}>{statusLabel}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading && <p className="text-sm text-slate-500 m-0">Loading…</p>}
        {error && <p className="text-sm text-red-600 m-0">{error}</p>}

        {!loading && !error && !current && !open && (
          <p className="text-sm text-slate-500 m-0">
            No Source of Wealth declaration stored for this patron yet. Request one from an ECDD.
          </p>
        )}

        {open && (
          <div className="rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 text-sm text-amber-950">
            <div className="font-medium">Open SoW request</div>
            <div className="text-xs mt-0.5 text-amber-900/80">
              Status: {open.status} · Due {fmtDate(open.dueAt)}
              {open.ecddId && (
                <>
                  {' '}
                  ·{' '}
                  <Link
                    className="underline font-medium"
                    to={`/compliance-safe/ecdd/${encodeURIComponent(open.ecddId)}`}
                  >
                    {open.ecddId}
                  </Link>
                </>
              )}
            </div>
          </div>
        )}

        {current && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <div>
              <div className="text-xs text-slate-500">Source of funds</div>
              <div className="font-medium text-slate-900">
                {current.sofCategory === 'other' || current.sofCategory === 'Other'
                  ? `Other — ${current.sofOtherDetail || ''}`
                  : current.sofCategory || '—'}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Source of wealth</div>
              <div className="font-medium text-slate-900">{current.sowCategory || '—'}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Occupation</div>
              <div className="font-medium text-slate-900">{current.occupation || '—'}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Employer</div>
              <div className="font-medium text-slate-900">{current.employer || '—'}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Captured</div>
              <div className="font-medium text-slate-900">
                {fmtDate(current.capturedAt)} · {current.source || '—'}
              </div>
            </div>
            {current.ecddId && (
              <div>
                <div className="text-xs text-slate-500">Originating ECDD</div>
                <Link
                  className="font-medium text-teal-800 underline"
                  to={`/compliance-safe/ecdd/${encodeURIComponent(current.ecddId)}`}
                >
                  {current.ecddId}
                </Link>
              </div>
            )}
            {current.narrative && (
              <div className="sm:col-span-2">
                <div className="text-xs text-slate-500">Details</div>
                <div className="text-slate-800 whitespace-pre-wrap">{current.narrative}</div>
              </div>
            )}
          </div>
        )}

        {history.length > 0 && (
          <details className="text-sm">
            <summary className="cursor-pointer text-slate-600 font-medium">
              Prior versions ({history.length})
            </summary>
            <ul className="mt-2 space-y-1.5 pl-0 list-none">
              {history.map((h) => (
                <li key={h.id} className="rounded border border-slate-100 bg-slate-50 px-2.5 py-1.5 text-xs text-slate-700">
                  {fmtDate(h.capturedAt)} · {h.sofCategory || h.sowCategory || h.source}
                  {h.ecddId ? ` · ${h.ecddId}` : ''}
                </li>
              ))}
            </ul>
          </details>
        )}
      </CardContent>
    </Card>
  )
}
